import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Crosshair } from 'lucide-react';
import { PageLayout } from '../../components/organisms/PageLayout';
import { ResumeTile } from '../../components/molecules/resume/ResumeTile';
import ConfirmDeleteModal from '../../components/organisms/ConfirmDeleteModal';
import { api } from '../../utils/api';
import { API_ENDPOINTS } from '../../config';
import '../../styles/resume-builder.css';
import { emitSubscriptionRefresh } from '../../utils/subscriptionEvents';
import { track } from '../../utils/analytics';

const breadcrumbItems = [
  { label: 'Resume Builder', href: '/resume-builder' },
  { label: 'Base Resumes' }
];

type TabKey = 'base' | 'tailored';

interface CV {
  uid: string;
  title: string;
  created_at: string;
  updated_at?: string | null;
}

const ResumeBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('base');
  const [cvs, setCvs] = useState<CV[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    cvId: string | null;
    cvTitle: string;
  }>({
    isOpen: false,
    cvId: null,
    cvTitle: '',
  });

  useEffect(() => {
    document.title = 'OfferFlow | Resume Builder';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchCVs = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await api.get<CV[]>(API_ENDPOINTS.cvs.list);
        if (cancelled) return;
        setCvs(data || []);
      } catch (err) {
        if (cancelled) return;
        setError('Failed to load resumes. Please try again.');
        setCvs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCVs();
    return () => { cancelled = true; };
  }, []);

  const handleDeleteClick = (id: string, title: string) => {
    setDeleteModal({
      isOpen: true,
      cvId: id,
      cvTitle: title,
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.cvId) {
      setDeleteModal({ isOpen: false, cvId: null, cvTitle: '' });
      return;
    }

    try {
      await api.delete(API_ENDPOINTS.cvs.delete(deleteModal.cvId));
      track('resume_deleted');
      setCvs(cvs.filter((cv) => cv.uid !== deleteModal.cvId));

      emitSubscriptionRefresh();
      
      setDeleteModal({ isOpen: false, cvId: null, cvTitle: '' });
    } catch (error) {
      setDeleteModal({ isOpen: false, cvId: null, cvTitle: '' });
    }
  };

  const handleCancelDelete = () => {
    setDeleteModal({ isOpen: false, cvId: null, cvTitle: '' });
  };

  const handleDuplicate = async (cvUid: string) => {
    try {
      const newCv = await api.post<CV>(API_ENDPOINTS.cvs.duplicate(cvUid), undefined);
      
      if (newCv && newCv.uid) {
        track('resume_duplicated');
        setCvs([...cvs, newCv]);

        emitSubscriptionRefresh();
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to duplicate resume. Please try again.');
    }
  };

  return (
    <PageLayout breadcrumbItems={breadcrumbItems}>
      <div className="resume-builder">
        <div className="resume-builder__cards">
          <div className="resume-type-card">
            <div className="resume-type-card__icon resume-type-card__icon--base">
              <FileText size={32} />
            </div>
            <div className="resume-type-card__content">
              <h3 className="resume-type-card__title">Base Resume</h3>
              <p className="resume-type-card__description">
                A main resume targeted to a specific role/title and seniority. We suggest you create one for each role you are targeting.
              </p>
              <button
                className="resume-type-card__button"
                onClick={() => navigate('/resume-builder/new-base-resume')}
              >
                Create New <span>→</span>
              </button>
            </div>
          </div>

          <div className="resume-type-card resume-type-card--coming-soon" title="Coming soon">
            <div className="resume-type-card__icon resume-type-card__icon--tailored">
              <Crosshair size={32} />
            </div>
            <div className="resume-type-card__content">
              <h3 className="resume-type-card__title">Job Tailored Resume</h3>
              <p className="resume-type-card__description">
                A resume targeted to a specific job description and built off of a Base Resume.
              </p>
              <button
                type="button"
                className="resume-type-card__button"
                disabled
                aria-disabled="true"
                onClick={(e) => e.preventDefault()}
              >
                Create New <span>→</span>
              </button>
            </div>
            <span className="resume-type-card__coming-soon">Coming soon</span>
          </div>
        </div>

        <div className="resume-builder__tabs">
          <button
            className={`resume-builder__tab ${activeTab === 'base' ? 'resume-builder__tab--active' : ''}`}
            onClick={() => setActiveTab('base')}
          >
            <span className="resume-builder__tab-icon">
              <FileText size={14} />
            </span>
            Base Resumes
          </button>
          <button
            className={`resume-builder__tab ${activeTab === 'tailored' ? 'resume-builder__tab--active' : ''}`}
            onClick={() => setActiveTab('tailored')}
          >
            <span className="resume-builder__tab-icon">
              <Crosshair size={14} />
            </span>
            Job Tailored Resumes
          </button>
        </div>

        <div className="resume-builder__content">
          {loading ? (
            <div className="resume-builder__loading">
              <p>Loading resumes...</p>
            </div>
          ) : error ? (
            <div className="resume-builder__error">
              <p>{error}</p>
              <button 
                className="resume-builder__empty-button"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : activeTab === 'base' && (
            <>
              {cvs.length === 0 ? (
                <div className="resume-builder__empty">
                  <p className="resume-builder__empty-text">
                    Looks like you haven't created any resumes yet. No worries! This is where your journey to your dream job begins.
                  </p>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="resume-builder__empty-button"
                      onClick={() => navigate('/resume-builder/new-base-resume')}
                    >
                      Build Your Resume
                    </button>
                    <button
                      className="resume-builder__empty-button"
                      style={{ background: 'transparent', border: '1px solid currentColor', opacity: 0.7 }}
                      onClick={() => navigate('/resume-builder/new-base-resume?blank=1')}
                    >
                      Create Blank CV
                    </button>
                  </div>
                </div>
              ) : (
                <div className="resume-builder__list">
                  {cvs.map((cv) => (
                    <ResumeTile
                      key={cv.uid}
                      id={cv.uid}
                      title={cv.title}
                      createdAt={cv.created_at}
                      updatedAt={cv.updated_at}
                      onOpen={(id) => navigate(`/resume-builder/editor/${id}`)}
                      onEdit={(id) => navigate(`/resume-builder/editor/${id}`)}
                      onDuplicate={(id) => handleDuplicate(id)}
                      onDelete={(id) => handleDeleteClick(id, cv.title)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'tailored' && (
            <div className="resume-builder__empty">
              <p className="resume-builder__empty-text">
                Job Tailored Resumes are coming soon. Stay tuned!
              </p>
              <button
                className="resume-builder__empty-button"
                disabled
                aria-disabled="true"
              >
                Coming Soon
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={deleteModal.isOpen}
        itemName={deleteModal.cvTitle}
        itemType="Resume"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </PageLayout>
  );
};

export default ResumeBuilder;
